import { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  CornerDownLeft,
  Loader2,
  MessageSquareText,
  Sparkles,
  Wrench,
  X,
} from "lucide-react";
import {
  streamChat,
  type ChatMessage,
  type ChatToolCallEvent,
  type ChatToolResultEvent,
} from "../../lib/api.js";
import type { Route } from "../../lib/useRoute.js";

/**
 * Slide-out chat rail. Streams Claude responses via the ui-server's /api/chat
 * SSE proxy. The assistant now has TOOL USE — when it invokes a server-side
 * tool (e.g. `enrich_keyword`, `analyze_url`), we render a pill inline in the
 * bubble so the operator sees what it's doing.
 *
 * Conversation shape:
 *   - user messages are simple strings
 *   - assistant messages are arrays of segments: text chunks + tool invocations
 *   (so a single AI turn can intermix "I'll look this up" → tool_call →
 *   tool_result → "Here's what I found")
 */

const SUGGESTIONS: Record<Route, string[]> = {
  video: [
    "אילו מאמרים מחכים לריל ומה השלב הבא שלהם?",
    "כמה עלה כל ריל שצילמנו עד היום?",
    "יש קליפים בספרייה שהיינו יכולים למחזר?",
  ],
  automations: [
    "Which automations are off that should be on?",
    "מה רץ אוטומטית ומה עדיין ידני?",
    "Did last night's syncs actually run?",
  ],
  offerings: [
    "מה קופות החולים מציעות ומה חסר להן?",
    "What happens to the price after the 20-session quota?",
    "Which methods do we cover that the funds do not?",
  ],
  folded: [
    "אילו וריאנטים קופלו בטעות?",
    "Which folded variants are a practitioner, not a method?",
    "What does a secondary keyword actually affect?",
  ],
  models: [
    "Which model writes the article body?",
    "איזה מודל אחראי על כל שלב?",
    "Where would a cheaper model actually save money?",
  ],
  overview: [
    "What should I work on next?",
    "מה הכי דחוף לטפל בו עכשיו?",
    "Which cluster is furthest from its target?",
  ],
  tasks: [
    "Run through the top 3 tasks I should batch together.",
    "תעשיר את כל הקיוורדים בהמתנה שמחכים לזה",
    "Which tasks are cheapest to clear?",
  ],
  sources: [
    "Analyze https://en.wikipedia.org/wiki/Reflexology — is it worth covering?",
    "תנתח את הקישור הזה ותגיד לי אם יש פער",
    "Promote discovery #1 if the recommendation is strong.",
  ],
  research: [
    "Expand רפלקסולוגיה into related keywords with volume.",
    "מה הקיוורדים עם הכי הרבה חיפושים שכבר חקרנו?",
    "Which researched keywords aren't in the pipeline yet?",
  ],
  pipeline: [
    "Show me keywords ready to produce.",
    "איזה קיוורד הכי קרוב להפקה?",
    "Compare reflexology vs naturopathy progress.",
  ],
  content: [
    "Which articles are stalest and need a refresh?",
    "איזה כתבות הכי ישנות ודורשות רענון?",
  ],
  "content-db": [
    "How many live articles are in the Reflexology cluster?",
    "Which mirrored articles have no cluster assigned?",
  ],
  entities: [
    "Which entities are flagged do-not-mention?",
    "Import my therapist roster CSV.",
    "List competitors without a known social handle.",
  ],
  calendar: [
    "מה יוצא השבוע, ואיפה יש חורים?",
    "למה נבחרו המאמרים בהצעה של השבוע הבא?",
    "אילו מאמרים שלנו מתאימים לחנוכה, ובאיזו זווית?",
    "תן לי 3 רעיונות לפוסטים סביב המעבר לשעון חורף",
    "מה עולה בגוגל טרנדס בנושא שינה?",
  ],
  distribution: ["Draft a Brevo subject line for the latest article."],
  audience: ["How many active contacts are on the master list?"],
  audit: ["Run a site audit", "Which pages have the most SEO issues?", "Show me pages with missing meta descriptions."],
  credentials: ["Which integrations am I missing?"],
  errors: ["What pipeline steps failed in the last 24h?", "Show unresolved upload errors."],
  spend: ["How much have we spent this month?", "Which provider is costing the most?"],
  brand: ["Which brand files haven't been touched in months?", "Show me the canonical name variants we catch."],
  help: ["How do I produce my first article?", "Walk me through cluster expansion."],
};

interface ToolSegment {
  type: "tool";
  id: string;
  name: string;
  input: Record<string, unknown>;
  /** undefined while pending, then ok/summary once result lands. */
  result?: { ok: boolean; summary: string };
}
interface TextSegment {
  type: "text";
  text: string;
}
type Segment = TextSegment | ToolSegment;

interface AssistantTurn {
  role: "assistant";
  segments: Segment[];
}
interface UserTurn {
  role: "user";
  content: string;
}
type Turn = UserTurn | AssistantTurn;

export function ChatPanel({ route, onClose }: { route: Route; onClose: () => void }) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<(() => void) | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // ESC closes; cleanup on unmount
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !streaming) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, streaming]);

  useEffect(() => {
    inputRef.current?.focus();
    return () => abortRef.current?.();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [turns, streaming]);

  function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || streaming) return;
    setError(null);
    const userTurn: UserTurn = { role: "user", content: trimmed };
    const assistantTurn: AssistantTurn = { role: "assistant", segments: [] };
    setTurns((prev) => [...prev, userTurn, assistantTurn]);
    setDraft("");
    setStreaming(true);

    // Convert turns → flat ChatMessage[] for the API. Assistant turns collapse to a
    // single text content (tool calls are server-managed inside the loop and don't
    // need to be replayed in subsequent calls — Anthropic accepts plain text history).
    const flatHistory: ChatMessage[] = turnsToChatHistory([...turns, userTurn]);

    // Helper to mutate the LAST (assistant) turn's segments.
    const updateLastAssistant = (mutate: (segs: Segment[]) => Segment[]) => {
      setTurns((prev) => {
        const out = [...prev];
        const last = out[out.length - 1];
        if (last && last.role === "assistant") {
          out[out.length - 1] = { ...last, segments: mutate(last.segments) };
        }
        return out;
      });
    };

    // Pending text segment we append to as tokens stream.
    let currentTextRef = { current: "" };
    const flushText = (segs: Segment[]) => {
      if (currentTextRef.current.length > 0) {
        const text = currentTextRef.current;
        currentTextRef.current = "";
        return [...segs, { type: "text", text } as TextSegment];
      }
      return segs;
    };

    const cleanup = streamChat(
      { messages: flatHistory, page: route },
      {
        onToken: (t) => {
          currentTextRef.current += t;
          updateLastAssistant((segs) => {
            // Coalesce into the trailing text segment if one is still pending.
            const last = segs[segs.length - 1];
            if (last && last.type === "text") {
              return [...segs.slice(0, -1), { type: "text", text: last.text + t }];
            }
            return [...segs, { type: "text", text: t }];
          });
          currentTextRef.current = "";
        },
        onToolCall: (call: ChatToolCallEvent) => {
          updateLastAssistant((segs) => [
            ...flushText(segs),
            { type: "tool", id: call.id, name: call.name, input: call.input },
          ]);
        },
        onToolResult: (r: ChatToolResultEvent) => {
          updateLastAssistant((segs) =>
            segs.map((s) =>
              s.type === "tool" && s.id === r.id ? { ...s, result: { ok: r.ok, summary: r.summary } } : s,
            ),
          );
        },
        onDone: () => setStreaming(false),
        onError: (err) => {
          setError(err);
          setStreaming(false);
        },
      },
    );
    abortRef.current = cleanup;
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(draft);
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/10 backdrop-blur-[1px] animate-fade-in" onClick={onClose} aria-hidden />

      <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-white shadow-rail animate-slide-in-right sm:max-w-lg">
        <header className="flex items-center gap-3 border-b border-line px-4 py-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-brand-600 to-brand-800 text-white">
            <Sparkles className="h-4 w-4" strokeWidth={2.2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-ink">AlternaBe AI</div>
            <div className="text-2xs text-muted">Aware of your pipeline · can read + act via tools</div>
          </div>
          <button onClick={onClose} className="btn-icon" title="Close (Esc)" disabled={streaming}>
            <X className="h-4 w-4" />
          </button>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 scrollbar-thin">
          {turns.length === 0 && !streaming && (
            <WelcomeCard route={route} onSuggest={(s) => send(s)} />
          )}
          {turns.map((t, i) => t.role === "user" ? (
            <UserBubble key={i} content={t.content} />
          ) : (
            <AssistantBubble key={i} segments={t.segments} isLastStreaming={streaming && i === turns.length - 1} />
          ))}
          {error && (
            <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="border-t border-line bg-canvas-deep/40 px-4 py-3">
          <div className="rounded-xl bg-white p-2 ring-1 ring-line focus-within:ring-2 focus-within:ring-brand/30">
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={streaming ? "Streaming response…" : `Ask anything about ${route}… (Hebrew or English)`}
              rows={2}
              disabled={streaming}
              dir="auto"
              className="w-full resize-none border-0 bg-transparent text-sm placeholder:text-muted focus:outline-none disabled:opacity-50"
            />
            <div className="mt-1 flex items-center justify-between">
              <div className="text-2xs text-muted">
                <kbd className="rounded bg-line-soft px-1 py-0.5 font-mono">Enter</kbd> send · <kbd className="rounded bg-line-soft px-1 py-0.5 font-mono">⇧Enter</kbd> newline
              </div>
              <button
                onClick={() => send(draft)}
                disabled={!draft.trim() || streaming}
                className="btn-primary btn-sm gap-1.5"
              >
                Send
                <CornerDownLeft className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Rendering
// ───────────────────────────────────────────────────────────────────────────

function UserBubble({ content }: { content: string }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] rounded-2xl rounded-br-md bg-brand-700 px-3.5 py-2 text-sm text-white shadow-sm">
        {/* dir="auto" — lets the browser flip RTL/LTR based on the first strong
            directional character. Hebrew prompts render right-aligned, English left. */}
        <div className="whitespace-pre-wrap" dir="auto">{content}</div>
      </div>
    </div>
  );
}

function AssistantBubble({ segments, isLastStreaming }: { segments: Segment[]; isLastStreaming: boolean }) {
  return (
    <div className="flex items-start gap-2">
      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-600 to-brand-800 text-white">
        <Sparkles className="h-3 w-3" strokeWidth={2.4} />
      </div>
      <div className="max-w-[85%] flex-1 space-y-2">
        {segments.length === 0 && isLastStreaming && (
          <div className="flex items-center gap-2 rounded-2xl rounded-tl-md bg-canvas px-3.5 py-2 text-xs text-muted ring-1 ring-line">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>Thinking…</span>
          </div>
        )}
        {segments.map((s, i) => {
          if (s.type === "text") {
            return (
              <div key={i} className="rounded-2xl rounded-tl-md bg-canvas px-3.5 py-2 text-sm text-ink ring-1 ring-line">
                <div className="whitespace-pre-wrap leading-relaxed" dir="auto">{s.text}</div>
              </div>
            );
          }
          return <ToolPill key={s.id} segment={s} />;
        })}
      </div>
    </div>
  );
}

function ToolPill({ segment }: { segment: ToolSegment }) {
  const pending = segment.result === undefined;
  const ok = segment.result?.ok ?? false;

  const baseClass = pending
    ? "bg-info-soft ring-info/30 text-blue-700"
    : ok
    ? "bg-accent-soft ring-accent/30 text-emerald-700"
    : "bg-danger-soft ring-danger/30 text-red-700";

  return (
    <div className={`flex items-start gap-2 rounded-xl px-3 py-2 text-xs ring-1 ${baseClass}`}>
      <div className="mt-0.5">
        {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
          ok ? <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.4} /> :
          <AlertCircle className="h-3.5 w-3.5" strokeWidth={2.4} />
        }
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <Wrench className="h-3 w-3" />
          <code className="font-mono text-2xs font-semibold">{segment.name}</code>
          <span className="text-2xs text-muted">
            {summariseInput(segment.input)}
          </span>
        </div>
        {segment.result && (
          <div className="mt-1 text-2xs text-ink">{segment.result.summary}</div>
        )}
      </div>
    </div>
  );
}

function summariseInput(input: Record<string, unknown>): string {
  const keys = Object.keys(input);
  if (keys.length === 0) return "";
  const pairs = keys.slice(0, 3).map((k) => {
    const v = input[k];
    if (v === null || v === undefined) return null;
    const display = typeof v === "string" ? v : JSON.stringify(v);
    const short = display.length > 30 ? display.slice(0, 27) + "…" : display;
    return `${k}=${short}`;
  }).filter(Boolean);
  return pairs.join(" · ");
}

function WelcomeCard({ route, onSuggest }: { route: Route; onSuggest: (s: string) => void }) {
  const ideas = SUGGESTIONS[route] ?? [];
  return (
    <div className="space-y-3">
      <div className="rounded-xl bg-gradient-to-br from-brand-50 to-canvas p-4 ring-1 ring-brand/15">
        <div className="flex items-center gap-2 text-sm font-semibold text-brand-800">
          <MessageSquareText className="h-4 w-4" />
          Hi — I'm AlternaBe AI
        </div>
        <p className="mt-1.5 text-xs text-muted">
          I know your pipeline + clusters + discoveries, and I can act via tools:
          enrich a keyword, analyze a URL, generate a brief, promote a discovery.
          Just say what you want — Hebrew or English. Your request is the confirmation.
        </p>
      </div>
      <div>
        <div className="mb-1.5 px-1 text-2xs font-semibold uppercase tracking-wider text-muted">Try</div>
        <div className="space-y-1.5">
          {ideas.map((idea) => (
            <button
              key={idea}
              onClick={() => onSuggest(idea)}
              className="block w-full rounded-lg border border-line bg-white px-3 py-2 text-left text-xs text-ink transition hover:border-brand-200 hover:bg-brand-50"
            >
              {idea}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Compress in-component turn history → flat ChatMessage[] for the API call.
 * Assistant turns are flattened to text content only (tool calls are
 * server-side state and don't need to be replayed — Anthropic accepts a
 * simpler history when continuing the conversation).
 */
function turnsToChatHistory(turns: Turn[]): ChatMessage[] {
  return turns
    .filter((t) => {
      if (t.role === "user") return true;
      // Only keep assistant turns that have actual text. An assistant turn with
      // only tool segments and no final text wouldn't help the model on continuation.
      return t.segments.some((s) => s.type === "text" && s.text.trim().length > 0);
    })
    .map((t) => {
      if (t.role === "user") return { role: "user" as const, content: t.content };
      const text = t.segments
        .filter((s): s is TextSegment => s.type === "text")
        .map((s) => s.text)
        .join("");
      return { role: "assistant" as const, content: text };
    });
}
